import { ArrowLeft, Sparkle } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { cleanId } from '../../shared/clean-id.ts';
import { accountsQuery, api, chatsQuery, queryClient, sitesQuery, unwrap } from '../api.ts';
import { ErrorNotice } from '../common.tsx';
import { useChatAttachments } from '../use-chat-attachments.ts';
import { ChatComposer } from './chat-composer.tsx';
import { PendingRequests } from './pending-requests.tsx';
export function NewSite({ onDashboard }: { onDashboard?: () => void }) {
  const [message, setMessage] = useState('');
  const chatId = useRef<string | null>(null);
  const creatingSite = useRef<Promise<string> | null>(null);
  const request = useRef<{ signature: string; runId: string } | null>(null);
  const accounts = useQuery(accountsQuery);
  const sites = useQuery(sitesQuery);
  const chats = useQuery(chatsQuery);
  const navigate = useNavigate();
  const ensureChat = async () => {
    if (chatId.current) return chatId.current;
    if (!creatingSite.current)
      creatingSite.current = api
        .POST('/api/chats', { body: {} })
        .then((result) => {
          chatId.current = unwrap(result).id;
          return chatId.current;
        })
        .catch((error) => {
          creatingSite.current = null;
          throw error;
        });
    return creatingSite.current;
  };
  const attachments = useChatAttachments();
  const create = useMutation({
    mutationFn: async ({ text, ids }: { text: string; ids: string[] }) => {
      const id = await ensureChat();
      const signature = JSON.stringify({ text, attachmentIds: ids });
      if (request.current?.signature !== signature) request.current = { signature, runId: cleanId() };
      await attachments.upload(id, ids);
      return unwrap(
        await api.POST('/api/chats/{chatId}/messages', {
          params: { path: { chatId: id } },
          body: {
            message: text,
            runId: request.current.runId,
            attachmentIds: ids,
            source: 'new-site',
          },
        }),
      );
    },
    onSuccess: async (_, { ids }) => {
      attachments.clearSent(ids);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sites'] }),
        queryClient.invalidateQueries({ queryKey: ['chats'] }),
      ]);
      await navigate({
        to: '/',
        search: { chatId: chatId.current! },
      });
    },
  });
  const canStart =
    !create.isPending &&
    Boolean(message.trim() || attachments.ids.length) &&
    !attachments.blocked &&
    !accounts.isPending;
  return (
    <main className="flex flex-col h-full overflow-hidden min-[761px]:flex-row">
      <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden h-full [&_>_.new-site]:flex-1 [&_>_.new-site]:overflow-y-auto [&_>_.new-site]:w-full">
        <div className="new-site mx-auto my-0 px-7 max-w-197.5 pt-8.5 pb-22.5">
          {Boolean(sites.data?.length || chats.data?.length) && (
            <Link
              to="/"
              onClick={onDashboard}
              className="back-link inline-flex items-center gap-2 text-kumo-subtle text-[13px] mb-4 [&:hover]:text-kumo-default"
            >
              <ArrowLeft />
              Dashboard
            </Link>
          )}
          <PendingRequests className="mx-auto mb-6 max-w-lg" />
          <div className="px-0 text-center pt-11.5 pb-8 [&_h1]:mx-0 [&_h1]:mt-[19px] [&_h1]:mb-4 [&_h1]:text-[26px] [&_h1]:leading-[1.2] [&_h1]:font-semibold [&_h1]:tracking-[-0.8px] [&_p]:m-0 [&_p]:leading-[1.65] [&_p]:text-kumo-subtle [&_p]:text-[15px] min-[761px]:[&_h1]:mx-0 min-[761px]:[&_h1]:mt-0 min-[761px]:[&_h1]:text-[30px] min-[761px]:[&_h1]:leading-[1.2] min-[761px]:[&_h1]:font-semibold min-[761px]:[&_h1]:tracking-[-0.8px]">
            <span className="border border-app-accent-border w-13 h-13 mb-4 inline-grid place-items-center bg-app-accent-surface rounded-[14px] text-kumo-brand">
              <Sparkle size={26} />
            </span>
            <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]" id="new-site-heading">
              Tell us about your business.
            </h1>
            <p>Start with one or two sentences. Our AI assistant will ask for any other details it needs.</p>
          </div>
          <ChatComposer
            value={message}
            onChange={setMessage}
            onSubmit={() =>
              create.mutate({
                text: message.trim() || 'Help me create a website using the attached materials.',
                ids: attachments.ids,
              })
            }
            canSend={canStart}
            attachments={attachments}
            sending={create.isPending}
            label="Tell us about your business."
            autoFocus
          />
          {create.isPending && (
            <p role="status" className="mt-3 text-sm text-kumo-subtle">
              {attachments.uploading ? 'Uploading…' : 'Sending…'}
            </p>
          )}
          <ErrorNotice error={create.error ?? accounts.error} />
        </div>
      </div>
    </main>
  );
}
